import { useCallback, useEffect, useRef, useState } from 'react';

// The browser side of Settings > Audio: the microphones the browser can
// see, its microphone permission, and a live level test. Nothing is
// recorded - the test only reads the input level.
export function useBrowserMicrophone() {
  const [microphones, setMicrophones] = useState([]);
  // granted | denied | prompt | unknown | checking
  const [permission, setPermission] = useState('checking');
  const [testing, setTesting] = useState(false);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState('');
  const stopRef = useRef(null);

  const loadDevices = useCallback(() => {
    if (!navigator.mediaDevices?.enumerateDevices) return Promise.resolve();
    return navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => setMicrophones(devices.filter((d) => d.kind === 'audioinput')))
      .catch(() => setMicrophones([]));
  }, []);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  useEffect(() => {
    if (!navigator.permissions?.query) {
      setPermission('unknown');
      return undefined;
    }

    let status;
    navigator.permissions
      .query({ name: 'microphone' })
      .then((result) => {
        status = result;
        setPermission(result.state);
        result.onchange = () => setPermission(result.state);
      })
      .catch(() => setPermission('unknown'));

    return () => {
      if (status) status.onchange = null;
    };
  }, []);

  const stopTest = useCallback(() => {
    stopRef.current?.();
    stopRef.current = null;
    setTesting(false);
    setLevel(0);
  }, []);

  useEffect(() => () => stopRef.current?.(), []);

  const startTest = useCallback(
    async (deviceId) => {
      setError('');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        });
        setPermission('granted');
        // Labels are only exposed once permission is granted.
        loadDevices();

        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioContextClass();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        audioContext.createMediaStreamSource(stream).connect(analyser);

        const data = new Uint8Array(analyser.frequencyBinCount);
        let frame;
        const tick = () => {
          analyser.getByteFrequencyData(data);
          const average = data.reduce((sum, value) => sum + value, 0) / data.length;
          setLevel(Math.min(1, (average / 255) * 2));
          frame = requestAnimationFrame(tick);
        };
        tick();

        stopRef.current = () => {
          cancelAnimationFrame(frame);
          stream.getTracks().forEach((track) => track.stop());
          audioContext.close().catch(() => {});
        };
        setTesting(true);
      } catch (err) {
        if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
          setPermission('denied');
        }
        setError(err?.message || 'Unable to access the microphone.');
      }
    },
    [loadDevices]
  );

  return { microphones, permission, testing, level, error, startTest, stopTest };
}
